import React, { useState } from "react";
import { Link } from "react-router-dom";
import { KenoGrid } from "../KenoGrid";
import { finEssai, type EtatEssai } from "../../helpers/essai";
import { grilleAuHasard, suivreListe } from "../../helpers/suivreListe";
import { useFavoriteCombinations } from "../../helpers/useFavoriteCombinations";
import { trackActivationFunnel, trackFirstValueAction, trackSetPlayedNumbers } from "../../helpers/analytics";
import { GrilleEnregistree } from "./GrilleEnregistree";
import { PremiersPas } from "./PremiersPas";
import styles from "./PremiereGrille.module.css";

/**
 * Premier écran après l'inscription (/home/?bienvenue=1, compte sans grille) : « Votre première
 * grille, en 30 secondes ». Trois choix, une seule action : reprendre La Synthèse du soir (lue
 * par le serveur), saisir ses numéros, ou une grille au hasard. Puis la confirmation et le choix
 * du mail de résultat. Aucune promesse : une liste reprise, pas une grille « à jouer ».
 */
type Choix = "synthese" | "saisie" | "hasard";
const MIN = 4;
const MAX = 10;

export function PremiereGrille({
  essai,
  prochain,
  notification,
  mailRefuse = false,
  onEnregistree,
  onChoisi,
}: {
  essai: EtatEssai | null;
  /** Jour du prochain tirage : rappelé en pied pour un compte sans essai (l'en-tête est masqué). */
  prochain: "ce soir" | "demain";
  notification: string | null | undefined;
  /** « Non merci » déjà répondu au choix du mail : la question n'est plus reposée. */
  mailRefuse?: boolean;
  onEnregistree: () => void;
  onChoisi?: (canal: "email" | "none") => void;
}) {
  const { create } = useFavoriteCombinations(true);
  const [actif, setActif] = useState<Choix | null>(null);
  const [selection, setSelection] = useState<number[]>([]);
  const [hasard, setHasard] = useState<number[]>([]);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [faite, setFaite] = useState<{ nom: string; numeros: number[] } | null>(null);

  const terminer = (nom: string, numeros: number[], source: string) => {
    setFaite({ nom, numeros });
    trackSetPlayedNumbers(numeros.length);
    trackActivationFunnel("mon_keno_setup");
    trackFirstValueAction(source);
    onEnregistree();
  };

  const enregistrer = async (nom: string, numeros: number[], source: string) => {
    setOccupe(true);
    setErreur(null);
    try {
      const g = await create({ name: nom, numbers: numeros, source });
      terminer(g.name, g.numbers, source);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : "La grille n'a pas pu être enregistrée.");
    } finally {
      setOccupe(false);
    }
  };

  const choisir = async (id: string) => {
    setErreur(null);
    if (id === "synthese") {
      setActif("synthese");
      setOccupe(true);
      const r = await suivreListe("synthese", { source: "premier_ecran_synthese" });
      setOccupe(false);
      if ((r.statut === "creee" || r.statut === "remplacee") && r.grille) {
        terminer(r.grille.name, r.grille.numbers, "premier_ecran_synthese");
      } else {
        setErreur(r.error ?? "La Synthèse n'a pas pu être enregistrée.");
      }
      return;
    }
    if (id === "hasard") {
      setHasard(grilleAuHasard(MAX));
      setActif("hasard");
      return;
    }
    setActif("saisie");
  };

  const pied = essai
    ? `Premium offert ${finEssai({ ...essai, jour: null, total: null })}, sans carte bancaire. Ensuite, votre compte reste gratuit.`
    : `Prochain tirage ${prochain} à 20 h : votre score s'affiche dès le résultat.`;

  if (faite) {
    return (
      <PremiersPas titre="C'est prêt" texte="Votre grille est dans Mes grilles : à chaque tirage, vous voyez combien de vos numéros sont sortis." choix={[]} actif={null} onChoix={() => {}} pied={pied}>
        <GrilleEnregistree nom={faite.nom} numeros={faite.numeros} notification={notification} mailRefuse={mailRefuse} onChoisi={onChoisi} />
        <Link className={styles.lien} to="/mon-keno/">Ouvrir Mes grilles</Link>
      </PremiersPas>
    );
  }

  return (
    <PremiersPas
      titre={"Votre première grille, en 30\u00a0secondes"}
      texte="Choisissez vos numéros une fois : à chaque tirage, vous voyez combien sont sortis."
      choix={[
        { id: "synthese", label: "Reprendre La Synthèse du soir", detail: "Notre liste signature, enregistrée telle quelle" },
        { id: "saisie", label: "Saisir mes numéros habituels", detail: `${MIN} à ${MAX} numéros, sur la grille de 56` },
        { id: "hasard", label: "Une grille au hasard", detail: `${MAX} numéros tirés au hasard` },
      ]}
      actif={actif}
      onChoix={(id) => void choisir(id)}
      occupe={occupe}
      pied={pied}
    >
      {actif === "saisie" && (
        <>
          <KenoGrid
            selectedNumbers={selection}
            onToggleNumber={(n) => setSelection((p) => (p.includes(n) ? p.filter((x) => x !== n) : p.length >= MAX ? p : [...p, n]))}
            maxSelection={MAX}
          />
          <div className={styles.actions}>
            <button type="button" className="km-btn" disabled={occupe || selection.length < MIN} onClick={() => void enregistrer("Mes numéros", selection, "premier_ecran_saisie")}>
              {selection.length < MIN ? `Choisissez encore ${MIN - selection.length} numéro${MIN - selection.length > 1 ? "s" : ""}` : `Enregistrer mes ${selection.length} numéros`}
            </button>
          </div>
        </>
      )}
      {actif === "hasard" && hasard.length > 0 && (
        <>
          <p className={styles.boules} aria-label={`Numéros tirés : ${hasard.join(", ")}`}>
            {hasard.map((n) => <span key={n} aria-hidden="true">{n}</span>)}
          </p>
          <div className={styles.actions}>
            <button type="button" className="km-btn" disabled={occupe} onClick={() => void enregistrer("Ma grille au hasard", hasard, "premier_ecran_hasard")}>Enregistrer cette grille</button>
            <button type="button" className="km-btn km-btn--ghost" disabled={occupe} onClick={() => setHasard(grilleAuHasard(MAX))}>Tirer une autre grille</button>
          </div>
        </>
      )}
      {actif === "synthese" && occupe && <p className={styles.attente} role="status">Enregistrement de La Synthèse…</p>}
      {erreur && <p className={styles.erreur} role="alert">{erreur}</p>}
    </PremiersPas>
  );
}
